import { useParams } from 'react-router-dom';
import { Table, Loader, Group } from '@mantine/core';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { fetchDispensersList, IDispenser } from '../../../libraries/dispensers';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import Header from '../../components/util/header';
import AddEditDispenser from './add-edit-dispenser';
import { Language } from '../../languages/languageHandler';
import PaginationComponent from '../../components/pagination';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import './styles.scss';

const Dispensers = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const params = useParams<{ eid?: string }>();
  const controller = useRef(new AbortController()).current;

  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const { limit = 10, setLimit, tableRef } = useFitTableRows({ bufferRows: 1, minHeightForDynamic: 400 });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [dispensers, setDispensers] = useState<IDispenser[]>([]);

  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'new' | 'edit'>('edit');
  const [activeDispenser, setActiveDispenser] = useState<IDispenser | undefined>(undefined);

  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();
  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    loadDispenserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.eid]);

  useEffect(() => {
    if (filterBarData && limit) {
      loadDispenserList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  // Load dispenser list
  const loadDispenserList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setDispensers([]);
    setTotalRows(0);

    fetchDispensersList(controller, Number(params.eid) || 0)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setDispensers(response?.data?.list || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openEditModal = (dispenser: IDispenser) => {
    setActiveDispenser(dispenser);
    setModalMode('edit');
    setModalOpened(true);
  };

  const openNewModal = () => {
    setActiveDispenser(undefined);
    setModalMode('new');
    setModalOpened(true);
  };

  const changePage = (page: number) => {
    setOffset((page - 1) * limit);
    setCurrentPage(page);
  };

  const resultsSelect = (newValue: number) => {
    setLimit(Number(newValue));
    setCurrentPage(1);
    setOffset(0);
  };

  return (
    <div className="dispensers-list">
      <Header pageTitle={lang.DISPENSERS_TITLE} />
      <FilterBar onDataChange={handleFilterBarOnDataChange} onSearch={loadDispenserList} addButton={openNewModal} />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>EID</Table.Th>
              <Table.Th>{lang.DISPENSER_ID}</Table.Th>
              <Table.Th>{lang.DISPENSER_PRODUCT}</Table.Th>
              <Table.Th>{lang.DISPENSER_STATUS}</Table.Th>
              <Table.Th>{lang.DISPENSER_QUANTITY}</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : !dispensers.length ? (
              <Table.Tr>
                <Table.Td colSpan={5} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              dispensers?.map((dispenser, index) => (
                <Table.Tr key={index} onClick={() => openEditModal(dispenser)}>
                  <Table.Td>{dispenser.eid}</Table.Td>
                  <Table.Td>{dispenser.dispenserid}</Table.Td>
                  <Table.Td>{`${dispenser.productid} - ${dispenser.productName}`}</Table.Td>
                  <Table.Td>
                    {dispenser.status === 0
                      ? lang.EQUIPMENT_STATE_BUTTON_IN_SERVICE
                      : lang.EQUIPMENT_STATE_BUTTON_OUT_OF_SERVICE}
                  </Table.Td>
                  <Table.Td>{dispenser.quantity}</Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>

      {/* Add/Edit Dispenser Modal */}
      {modalOpened && (
        <AddEditDispenser
          opened={modalOpened}
          close={() => setModalOpened(false)}
          mode={modalMode}
          dispenser={activeDispenser}
          eid={Number(params.eid) || 0}
          onSaved={loadDispenserList}
        />
      )}
    </div>
  );
};

export default Dispensers;
